/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/Reviewers" />
import { IPullRequestPolicyEvaluation, IReviewer, PullRequestMessageType, ReviewerVote } from "Contracts/PullRequest";
import * as React from "react";
import { PullRequestStatus } from "Repos/Common/Contracts/Git";
import { getDisplayNameFromIdentity } from "Repos/Common/Util/Identity";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { css, useComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZone } from "VSSUI/FocusZone";
import { Header } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon, IconSize } from "VSSUI/Icon";
import { IdentityPickerDropdown, IIdentity } from "VSSUI/IdentityPicker";
import { Link } from "VSSUI/Link";
import { IMenuItem, MenuButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { IPersonaProvider, Persona, PersonaSize } from "VSSUI/Persona";
import { Status, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { KeyCode } from "VSSUI/Util";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import { hasPermission, PullRequestPermissions } from "../../PullRequestPermissionService";
import { getPullRequestPolicyService } from "../../PullRequestPolicyService";
import { getPullRequestViewService, IPullRequestViewService } from "../../PullRequestViewService";

export class Reviewers extends VssComponent {
    private readonly identityPickerProvider = new PeoplePickerProvider(this.context.pageContext);
    private readonly optionalIdentityPickerVisible = new ObservableValue<boolean>(false);
    private readonly requiredIdentityPickerVisible = new ObservableValue<boolean>(false);
    private readonly isCollapsedDeclinedReviewers = new ObservableValue(true);

    public render() {
        const { pageContext } = this.context;
        const prDetailsService = getPullRequestViewService(pageContext);
        const policyService = getPullRequestPolicyService(pageContext);
        const personaProvider = prDetailsService.getPersonaProvider();
        const canUpdateReviewers = hasPermission(pageContext, PullRequestPermissions.UpdateReviewers);
        return (
            <Observer reviewers={prDetailsService.getReviewers()} reviewerPolicies={policyService.getAutomaticReviewerPolicies()}>
                {(observerProps: { reviewers: IReviewer[]; reviewerPolicies: IPullRequestPolicyEvaluation[] }) => {
                    const requiredReviewers = observerProps.reviewers.filter(x => x.baseReviewer.isRequired).sort(this.compareReviewers);
                    const nonRequiredReviewers = observerProps.reviewers.filter(x => !x.baseReviewer.isRequired).sort(this.compareReviewers);
                    const optionalReviewers = nonRequiredReviewers.filter(x => !x.baseReviewer.hasDeclined);
                    const declinedReviewers = nonRequiredReviewers.filter(x => x.baseReviewer.hasDeclined);

                    return (
                        <div className="flex-column">
                            <Observer status={prDetailsService.getStatus()}>
                                {(statusObserverProps: { status: PullRequestStatus }) => (
                                    <Header
                                        className="no-padding"
                                        commandBarItems={canUpdateReviewers ? this.getCommandBarItems(statusObserverProps.status) : undefined}
                                        title={Resources.Reviewers}
                                    />
                                )}
                            </Observer>

                            <ScreenSizeConditional condition={size => size < ScreenSize.small}>
                                <ReposMessages typeFilter={type => type == PullRequestMessageType.reviewersError} />
                            </ScreenSizeConditional>

                            <ReviewerSection
                                canDeclineReviewer={reviewerId => prDetailsService.canDeclineReviewer(reviewerId)}
                                canFlagReviewer={reviewerId => prDetailsService.canFlagReviewer(reviewerId)}
                                canUpdateReviewers={canUpdateReviewers}
                                className="pr-required-reviewers-section"
                                identityProvider={this.identityPickerProvider}
                                identityPickerVisible={this.requiredIdentityPickerVisible}
                                noReviewersMessage={Resources.NoRequiredReviewers}
                                onIdentityPickerBlur={() => (this.requiredIdentityPickerVisible.value = false)}
                                onIdentityPickerChange={result => this.onIdentityPickerChange(result, true)}
                                onToggleDeclineToReview={reviewer => prDetailsService.updateDeclineToReview(!reviewer.baseReviewer.hasDeclined)}
                                onToggleFlag={reviewer => prDetailsService.toggleReviewerFlag(reviewer)}
                                onRemoveReviewer={reviewer => prDetailsService.removeReviewer(reviewer.identity.id)}
                                personaProvider={personaProvider}
                                prStatus={prDetailsService.getStatus()}
                                reviewers={requiredReviewers}
                                title={Resources.Required}
                                toggleRequired={reviewer => prDetailsService.updateReviewerRequired(reviewer, false)}
                            />

                            <ReviewerSection
                                canDeclineReviewer={reviewerId => prDetailsService.canDeclineReviewer(reviewerId)}
                                canFlagReviewer={reviewerId => prDetailsService.canFlagReviewer(reviewerId)}
                                canUpdateReviewers={canUpdateReviewers}
                                className="pr-optional-reviewers-section"
                                identityProvider={this.identityPickerProvider}
                                identityPickerVisible={this.optionalIdentityPickerVisible}
                                noReviewersMessage={Resources.NoOptionalReviewers}
                                onIdentityPickerBlur={() => (this.optionalIdentityPickerVisible.value = false)}
                                onIdentityPickerChange={result => this.onIdentityPickerChange(result, false)}
                                onToggleDeclineToReview={() => prDetailsService.updateDeclineToReview(true)}
                                onToggleFlag={reviewer => prDetailsService.toggleReviewerFlag(reviewer)}
                                onRemoveReviewer={reviewer => prDetailsService.removeReviewer(reviewer.identity.id)}
                                personaProvider={personaProvider}
                                prStatus={prDetailsService.getStatus()}
                                reviewers={optionalReviewers}
                                title={Resources.Optional}
                                toggleRequired={reviewer => prDetailsService.updateReviewerRequired(reviewer, true)}
                            />

                            <Observer isCollapsed={this.isCollapsedDeclinedReviewers}>
                                {({ isCollapsed }: { isCollapsed: boolean }) => {
                                    return (
                                        declinedReviewers.length > 0 && (
                                            <ReviewerSection
                                                canDeclineReviewer={reviewerId => prDetailsService.canDeclineReviewer(reviewerId)}
                                                canFlagReviewer={reviewerId => prDetailsService.canFlagReviewer(reviewerId)}
                                                canUpdateReviewers={canUpdateReviewers}
                                                className="pr-declined-reviewers-section"
                                                onToggleDeclineToReview={() => prDetailsService.updateDeclineToReview(false)}
                                                onToggleFlag={reviewer => prDetailsService.toggleReviewerFlag(reviewer)}
                                                onRemoveReviewer={reviewer => prDetailsService.removeReviewer(reviewer.identity.id)}
                                                personaProvider={personaProvider}
                                                prStatus={prDetailsService.getStatus()}
                                                reviewers={isCollapsed ? [] : declinedReviewers}
                                                title={
                                                    <Link onClick={() => (this.isCollapsedDeclinedReviewers.value = !isCollapsed)} subtle={true}>
                                                        {format(Resources.DeclinedToReviewTitle, declinedReviewers.length)}
                                                    </Link>
                                                }
                                                toggleRequired={reviewer =>
                                                    prDetailsService.updateReviewerRequired(reviewer, !reviewer.baseReviewer.isRequired)
                                                }
                                            />
                                        )
                                    );
                                }}
                            </Observer>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    private compareReviewers = (a: IReviewer, b: IReviewer) => {
        if (a.vote !== ReviewerVote.NoVote && b.vote === ReviewerVote.NoVote) {
            return -1;
        }
        if (a.vote === ReviewerVote.NoVote && b.vote !== ReviewerVote.NoVote) {
            return 1;
        }
        return a.identity.displayName.localeCompare(b.identity.displayName, navigator.language);
    };

    private getCommandBarItems: (status: PullRequestStatus) => IHeaderCommandBarItem[] = (status: PullRequestStatus) => {
        return [
            {
                disabled: status !== PullRequestStatus.Active,
                id: "add-reviewer-menu",
                subMenuProps: {
                    id: "add-reviewer-submenu",
                    items: [
                        {
                            id: "add-optional",
                            onActivate: () => {
                                this.optionalIdentityPickerVisible.value = true;
                            },
                            text: Resources.OptionalReviewer
                        },
                        {
                            id: "add-required",
                            onActivate: () => {
                                this.requiredIdentityPickerVisible.value = true;
                            },
                            text: Resources.RequiredReviewer
                        }
                    ]
                },
                text: Resources.Add
            }
        ];
    };

    private onIdentityPickerChange = (identity: IIdentity | undefined, required: boolean) => {
        if (identity) {
            const prDetailsService = this.context.pageContext.getService<IPullRequestViewService>("IPullRequestViewService");
            prDetailsService.addReviewer(identity, required);
        }
        return false;
    };
}

interface IReviewerSectionProps {
    canDeclineReviewer: (reviewerId: string) => boolean;
    canFlagReviewer: (reviewerId: string) => boolean;
    canUpdateReviewers: boolean;
    className?: string;
    identityProvider?: IPeoplePickerProvider;
    identityPickerVisible?: IObservableValue<boolean>;
    noReviewersMessage?: string;
    onIdentityPickerBlur?: () => void;
    onIdentityPickerChange?: (item?: IIdentity) => void;
    onToggleDeclineToReview: (reviewer: IReviewer) => void;
    onToggleFlag: (reviewer: IReviewer) => void;
    onRemoveReviewer: (reviewer: IReviewer) => void;
    personaProvider: IPersonaProvider;
    prStatus: IReadonlyObservableValue<PullRequestStatus>;
    reviewers: IReviewer[];
    title?: React.ReactNode;
    toggleRequired: (reviewer: IReviewer) => void;
}

const ReviewerSection: React.FunctionComponent<IReviewerSectionProps> = props => {
    const {
        canDeclineReviewer,
        canFlagReviewer,
        canUpdateReviewers,
        className,
        identityProvider,
        identityPickerVisible,
        noReviewersMessage,
        onIdentityPickerBlur,
        onIdentityPickerChange,
        onToggleDeclineToReview,
        onToggleFlag,
        onRemoveReviewer,
        personaProvider,
        reviewers,
        title,
        toggleRequired
    } = props;

    const { pageContext } = useComponentContext();
    const currentUserId = pageContext.getService<IVssPageService>("IVssPageService").getData()!.user.id;

    return (
        <div className={css("flex-grow flex-column", className)}>
            {title && <div className="margin-bottom-8 body-s secondary-text font-weight-semibold">{title}</div>}
            {identityPickerVisible && (
                <Observer showIdentityPicker={identityPickerVisible}>
                    {(observerProps: { showIdentityPicker: boolean }) => {
                        return observerProps.showIdentityPicker ? (
                            <FocusZone focusOnMount={true}>
                                <div className="margin-bottom-8">
                                    <IdentityPickerDropdown
                                        className="repos-reviewers-identity-picker"
                                        onBlur={onIdentityPickerBlur}
                                        onChange={onIdentityPickerChange!}
                                        pickerProvider={identityProvider!}
                                        value={undefined}
                                    />
                                </div>
                            </FocusZone>
                        ) : (
                            reviewers.length === 0 && (
                                <div
                                        className="repos-pr-no-items-well cursor-pointer"
                                        onClick={() => (identityPickerVisible.value = true)}
                                        onKeyPress={event => {
                                            if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                                                identityPickerVisible.value = true;
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {noReviewersMessage}
                                </div>
                            )
                        );
                    }}
                </Observer>
            )}
            {reviewers.length !== 0 &&
                reviewers.map(reviewer => (
                    <Reviewer
                        canDecline={canDeclineReviewer(reviewer.identity.id)}
                        canFlag={canFlagReviewer(reviewer.identity.id)}
                        canUpdateReviewers={canUpdateReviewers}
                        currentUserId={currentUserId}
                        key={reviewer.identity.id}
                        onToggleDeclineToReview={() => onToggleDeclineToReview(reviewer)}
                        onToggleFlag={() => onToggleFlag(reviewer)}
                        onRemove={() => onRemoveReviewer(reviewer)}
                        personaProvider={personaProvider}
                        prStatus={props.prStatus}
                        required={reviewer.baseReviewer.isRequired}
                        reviewer={reviewer}
                        toggleRequired={toggleRequired}
                    />
                ))}
        </div>
    );
};

interface IReviewerProps {
    canUpdateReviewers: boolean;
    canDecline: boolean;
    canFlag: boolean;
    currentUserId: string;
    onToggleDeclineToReview: () => void;
    onToggleFlag: () => void;
    onRemove: () => void;
    personaProvider: IPersonaProvider;
    prStatus: IReadonlyObservableValue<PullRequestStatus>;
    required: boolean;
    reviewer: IReviewer;
    toggleRequired: (reviewer: IReviewer) => void;
}

function getReviewerMenuItems(props: IReviewerProps, status: PullRequestStatus): IMenuItem[] {
    const { canDecline, canFlag, canUpdateReviewers, onToggleDeclineToReview, onToggleFlag, onRemove, required, reviewer, toggleRequired } = props;

    let menuItems: IMenuItem[] = [];
    if (status === PullRequestStatus.Active) {
        const nonPolicyMenuItems: IMenuItem[] = [];
        if (canUpdateReviewers) {
            const { hasDeclined, isFlagged } = reviewer.baseReviewer;
            if (required || !hasDeclined) {
                nonPolicyMenuItems.push({
                    id: "make-required",
                    onActivate: () => {
                        toggleRequired(reviewer);
                    },
                    text: required ? Resources.MakeOptional : Resources.MakeRequired
                });
            }

            if (canFlag) {
                nonPolicyMenuItems.push({
                    id: "flag",
                    onActivate: onToggleFlag,
                    text: isFlagged ? Resources.Unflag : Resources.Flag
                });
            }

            if (canDecline) {
                nonPolicyMenuItems.push({
                    id: "decline",
                    onActivate: onToggleDeclineToReview,
                    text: hasDeclined ? Resources.RejoinToReview : Resources.DeclineToReview
                });
            } else {
                nonPolicyMenuItems.push({
                    id: "remove",
                    onActivate: onRemove,
                    text: Resources.Remove
                });
            }
        }

        const policyMenuItems: IMenuItem[] = [
            {
                href: reviewer.policyUrl,
                id: "view-policy",
                text: Resources.ViewPolicy
            }
        ];

        menuItems = nonPolicyMenuItems;
        if (reviewer.strictestPolicy) {
            if (reviewer.strictestPolicy.isBlocking && reviewer.baseReviewer.isRequired) {
                menuItems = policyMenuItems;
            } else {
                menuItems = [...nonPolicyMenuItems, ...policyMenuItems];
            }
        }
    }

    return menuItems;
}

const Reviewer: React.FunctionComponent<IReviewerProps> = props => {
    const { currentUserId, personaProvider, prStatus, reviewer } = props;

    return (
        <FocusWithin>
            {(focusWithinStatus: IFocusWithinStatus) => (
                <div
                    className={css("repos-reviewer flex-row flex-center", focusWithinStatus.hasFocus && "focused")}
                    onBlur={focusWithinStatus.onBlur}
                    onFocus={focusWithinStatus.onFocus}
                >
                    <Status {...reviewer.voteStatusProps} className="repos-reviewer-vote-icon icon-large-margin" size={StatusSize.m} />
                    <Persona className="icon-large-margin" identity={reviewer.identity} personaProvider={personaProvider} size={PersonaSize.size24} />

                    <div className="flex-column flex-grow text-ellipsis icon-large-margin">
                        <Tooltip overflowOnly>
                            <div className="body-m flex-grow text-ellipsis">{getDisplayNameFromIdentity(reviewer.baseReviewer)}</div>
                        </Tooltip>
                        <Tooltip overflowOnly>
                            <div className="body-s secondary-text text-ellipsis">{reviewer.voteText}</div>
                        </Tooltip>
                    </div>

                    {reviewer.strictestPolicy && (
                        <Icon
                            className="icon-large-margin"
                            iconName="Ribbon"
                            size={IconSize.medium}
                            tooltipProps={{
                                text: format(
                                    Resources.ReviewerAddedByPolicy,
                                    reviewer.strictestPolicy.message ? reviewer.strictestPolicy.message : ""
                                )
                            }}
                        />
                    )}

                    {reviewer.baseReviewer.isFlagged && reviewer.identity.id === currentUserId && (
                        <Icon
                            className="icon-large-margin repos-reviewer-flag-icon"
                            iconName="Flag"
                            size={IconSize.medium}
                            tooltipProps={{ text: Resources.Flagged }}
                        />
                    )}

                    <Observer prStatus={prStatus}>
                        {(observerProps: { prStatus: PullRequestStatus }) => {
                            const menuItems = getReviewerMenuItems(props, observerProps.prStatus);

                            if (menuItems.length > 0) {
                                return (
                                    <MenuButton
                                        className="repos-reviewer-reveal"
                                        contextualMenuProps={{
                                            menuProps: { id: "menu", items: menuItems }
                                        }}
                                        hideDropdownIcon={true}
                                        iconProps={{ className: "small", iconName: "MoreVertical" }}
                                        subtle={true}
                                    />
                                );
                            }

                            return null;
                        }}
                    </Observer>
                </div>
            )}
        </FocusWithin>
    );
};
